import { Injectable } from '@angular/core';
//import { Network } from '@ionic-native/network/ngx'
import { Network } from '@capacitor/network';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { LoadingController, Platform } from '@ionic/angular';
import { ConnectionStatus, RequestService, OfflineManager } from './shared';
import { MobileService } from './mobileLogin-api.service';
import { AppBase } from 'src/app/base-classes/app-base';
import { Translate } from 'src/shared/translate';
import { UptimeService } from './uptimeService.service';
import { Uptime } from '@ionic-native/uptime/ngx';
import { CacheTime } from './entities/cacheTime.class';
import * as moment from 'moment';
import { StorageService } from './storageservice';
import { DateTimeWebFormat, MomentDefaultFormat, DateTimeFormat } from '../shared/entities/constantsExport.class';
import { RollbarErrorHandler } from './rollbar.service';
import { Capacitor } from '@capacitor/core';

@Injectable()
export class NetworkService extends AppBase {

  private status: BehaviorSubject<ConnectionStatus> = new BehaviorSubject(ConnectionStatus.Offline);
  private isOnline = navigator.onLine ? 0 : 1;
  pingInterval: any;

  constructor(
    private plt: Platform,
    //private network: Network,
    private mobileService: MobileService,
    private requestService: RequestService,
    private offlineManager: OfflineManager,
    private uptimeService: UptimeService,
    private uptime: Uptime,
    private storage: StorageService,
    public loading: LoadingController,
    private rollbarErrorHandler: RollbarErrorHandler
  ) {
    super(loading);
    this.initLoader(Translate.getTranslation("Syncing"));

    this.plt.ready().then(async () => {
      this.isOnline = navigator.onLine ? 0 : 1;
      this.initializeNetworkEvents();
      const networkStatus = await Network.getStatus();
      let status = ConnectionStatus.Online;
      if (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios') {
        status = networkStatus.connected ? ConnectionStatus.Online : ConnectionStatus.Offline;
      }
      this.status.next(status);
    });
  }

  public async initializeNetworkEvents() {
    if (!this.mobileService.checkIfDesktop()) {
      const status = await Network.getStatus();
      this.updateNetworkStatus(status.connected ? ConnectionStatus.Online : ConnectionStatus.Offline);

      // Listen for network changes
      Network.addListener('networkStatusChange', (status) => {
        if (status.connected) {
          if (this.status.getValue() === ConnectionStatus.Offline) {
            this.syncOnConnect().then(() => {
              this.updateNetworkStatus(ConnectionStatus.Online);
            });
          }
        } else {
          if (this.status.getValue() === ConnectionStatus.Online) {
            this.updateNetworkStatus(ConnectionStatus.Offline);
          }
        }
      });
    }
    else {
      var self = this;
      fromEvent(window, "online").subscribe(() => {
        this.syncOnConnect().then(() => {
          self.isOnline = 0;
        });
      });

      fromEvent(window, "offline").subscribe(() => {
        self.isOnline = 1;
      });
    }
  }

  async syncOnConnect() {
    await this.initLoader(Translate.getTranslation("Syncing"));
    this.showLoader();
    return new Promise<any>(resolve => {
      this.syncTime().then(() => { }).catch(e => {
        console.log("error ", e);
      }).finally(() => {
        this.syncWithServer();
        resolve([]);
      })
    });
  }

  async syncTime() {
    return new Promise<any>((resolve, reject) => {
      if (this.uptimeService.isCordova()) {
        let utcTime = this.requestService.getUTCTime();
        let uptimeValue = this.uptime.getUptime(true);
        let cacheTimes = this.storage.get(this.uptimeService.cacheTimesKey);
        Promise.all([utcTime, uptimeValue, cacheTimes]).then(data => {
          var commonTime = data[0];
          var uptime = data[1];
          var timePoint = new CacheTime(-1, false, moment(commonTime, DateTimeWebFormat).format(DateTimeFormat), 0, parseInt(uptime), true, 0, false, "", 0);

          var cacheTimesArray = new Array<CacheTime>();
          if (data[2] != null) {
            cacheTimesArray = data;
          }

          cacheTimesArray = this.uptimeService.recalculateCacheTimeArray(cacheTimesArray, timePoint);
          this.storage.set(this.uptimeService.cacheTimesKey, cacheTimesArray).then(() => {
            resolve([]);
          }).catch(e => reject(e));
        }).catch(e => reject(e));
      } else { resolve([]); }
    });
  }

  public syncWithServer() {
    this.offlineManager.getCacheData().then(data => {
      if (!data.Worker) {
        this.updateNetworkStatus(ConnectionStatus.Online);
        this.hideLoader();
        return;
      }
      setTimeout(x => {
      }, 1000)
      if (this.requestService.workerData !== undefined) {
        this.requestService.sendCacheForSync(data).then(result => {
          if (result == 1) {
            this.offlineManager.getDataForCache().then(() => {
              this.hideLoader();
              this.uptimeService.removeFromCacheTimeArray();
              this.updateNetworkStatus(ConnectionStatus.Online);
            });
          } else {
            this.hideLoader();
            this.updateNetworkStatus(ConnectionStatus.Online);
          }
        });
      } else {
        this.hideLoader();
        this.updateNetworkStatus(ConnectionStatus.Online);
      }
    });

    this.rollbarErrorHandler.sendLogsToServer();
  }

  private async updateNetworkStatus(status: ConnectionStatus) {
    this.status.next(status);
    this.isOnline = navigator.onLine ? 0 : 1;
    this.requestService.pingServer().then(() => {
      this.userIsOnline2();
    });
  }

  public onNetworkChange(): Observable<ConnectionStatus> {
    return this.status.asObservable();
  }

  public getCurrentNetworkStatus(): ConnectionStatus {
    if (!this.mobileService.checkIfDesktop()) {
      return this.status.getValue();
    }
    return this.isOnline;
  }

  public userIsOnline(): boolean {
    return this.getCurrentNetworkStatus() == ConnectionStatus.Online;
  }

  public userIsOnline2() {
    var isOnline: boolean = this.getCurrentNetworkStatus() == ConnectionStatus.Online && !this.requestService.offlineMode;
    localStorage.setItem("RollbarIsOnline", isOnline.toString());
    return isOnline;
  }

  public pingPeriodically() {
    this.pingInterval = setInterval(() => {
      this.userIsOnline2();
      if (this.userIsOnline()) {
        var lastOfflineModeValue = this.requestService.offlineMode;
        this.requestService.pingServer().then(() => {
          if (!this.requestService.offlineMode && lastOfflineModeValue) {
            this.syncOnConnect();
            this.userIsOnline2();
          }
        });
      }
      clearInterval(this.pingInterval);
    }, 30000);
  }
}